import styled from "styled-components";
import { DesignTokens } from "melco-ui";
import { StatusDot } from "./item/StatusDot";
import { isMachineError } from "./statusResolvers";
import { SELECTED_BG, SELECTED_ACCENT } from "./summitTheme";
import machineIconUrl from "../../assets/icon_machine.svg";

const { spacings, colors } = DesignTokens;

type DisplaySummitDetailsMachineStatusProps = {
    filteredMachines: any[];
    onClick?: (selectedMachine: any, deviceId: string) => void;
    selectedMachine: any;
};

const MachineRow = styled.div<{ $selected?: boolean }>`
    display: flex;
    align-items: flex-start;
    padding: ${spacings.small}px  ${spacings.small}px ${spacings.small}px 0px;
    border-bottom: 1px solid ${colors.gray.middle};
    border-left: 3px solid transparent;
    cursor: pointer;
    transition: background-color 0.1s ease, border-color 0.1s ease;

    &:hover {
        background-color: ${colors.gray.light};
    }

    /* Same "selected" treatment as UserResultCard — a visible red tint
       plus a red accent bar — so the highlighted machine is obvious
       instead of blending into the un-selected rows around it. */
    ${({ $selected }) =>
        $selected &&
        `
            background-color: ${SELECTED_BG};
            border-left-color: ${SELECTED_ACCENT};
        `}
`;

// WHAT: the row's inner two-column layout (icon | name/id/last-scan).
// WHY a dedicated flex wrapper rather than putting these two columns
// directly inside MachineRow: MachineRow itself is already a flex row
// with the status dot pinned to its far right (justify-content:
// space-between) — nesting the icon+text pairing in its own flex
// container keeps "icon column sized to the icon, text column takes
// the rest" independent of that outer row/dot layout.
const MachineMain = styled.div`
    display: flex;
    align-items: flex-start;
    gap: ${spacings.xsmall}px;
    min-width: 0;
`;

// WHAT: the icon's column. No explicit width is set on purpose — a flex
// item with no flex-grow sizes itself to its content by default, so this
// column is exactly as wide as the icon (20px) and never wider, per the
// "left column width only up to icon width" requirement.
const MachineIconColumn = styled.div`
    flex-shrink: 0;
    display: flex;
    align-items: center;
`;

// Simple monitor glyph standing in for "this is a machine" — deliberately
// generic/schematic rather than a literal product photo, matching the
// plain-line-icon style already used for StatusDot elsewhere in this file.
// const MachineIcon: React.FC = () => (
//     <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
//         <rect x="3" y="4" width="18" height="12" rx="1.5" stroke={colors.gray.middle} strokeWidth="1.5" />
//         <path d="M9 20h6M12 16v4" stroke={colors.gray.middle} strokeWidth="1.5" strokeLinecap="round" />
//     </svg>
// );
const MachineIcon: React.FC = () => <img src={machineIconUrl} width={50} height={70} alt="" />;

const MachineTextColumn = styled.div`
    /* Takes whatever space is left after the icon column — this is what
       makes the text start flush at the left edge of its own column
       rather than being centered/pushed by the icon's width. */
    flex: 1;
    min-width: 0;
`;

const MachineNameText = styled.div<{ $selected?: boolean }>`
    font-size: 14px;
    font-weight: 500;
    color: ${({ $selected }) => ($selected ? SELECTED_ACCENT : colors.gray.dark)};
`;

const MachineIdText = styled.div`
    font-size: 11px;
    color: ${colors.gray.dark};
    margin-top: 1px;
`;

const LastScanText = styled.div`
    font-size: 11px;
    color: ${colors.gray.middle};
    margin-top: 2px;
`;


export const DisplaySummitDetailsMachineStatus: React.FC<
    DisplaySummitDetailsMachineStatusProps
> = ({ selectedMachine, filteredMachines, onClick }) => {
    return (
        <>
            {filteredMachines?.map((machine: any) => {
                const isSelected = selectedMachine?.device_id === machine.device_id;
                const isError = isMachineError(machine);
                const displayName = machine?.machine_name || machine?.device_id;

                return (
                    <MachineRow
                        key={machine.device_id}
                        $selected={isSelected}
                        onClick={() => onClick?.(machine, machine.device_id)}
                    >
                        <MachineMain>
                            <MachineIconColumn>
                                <MachineIcon />
                            </MachineIconColumn>
                            <MachineTextColumn>
                                <MachineNameText $selected={isSelected}>{displayName}</MachineNameText>
                                <MachineIdText>{machine.device_id}</MachineIdText>
                                {/* last_scan is an ISO date string from the machines-list API.
                                    Guarded with a null-check + try/catch-free Date parse: if the
                                    value is missing or unparseable we just omit the line instead
                                    of rendering "Invalid Date" to the rep. */}
                                {machine.last_scan && (
                                    <LastScanText>
                                        Last scan: {new Date(machine.last_scan).toLocaleString()}
                                    </LastScanText>
                                )}
                                {/* _owner is set locally in SummitWorkspace when a manager's
                                    machines are merged in alongside the selected user's own —
                                    not a field from the API itself. */}
                                {/* {machine._owner === "manager" && <OwnerTag>Manager's machine</OwnerTag>} */}
                            </MachineTextColumn>
                        </MachineMain>
                        <StatusDot variant={isError ? "error" : "success"} />
                    </MachineRow>
                );
            })}
        </>
    );
};
